import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, BookOpen, Users, Lock, Scale, AlertTriangle, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import './Terms.css';

const Terms: React.FC = () => {
  return (
    <div className="terms-container">
      {/* Header */}
      <div className="terms-header">
        <div className="terms-header-content">
          <div className="terms-header-nav">
            <Link to="/signup">
              <Button variant="outline" size="sm" className="terms-back-btn">
                <ArrowLeft className="w-4 h-4" />
                Back to Sign Up
              </Button>
            </Link>
            <div className="terms-logo-section">
              <img
                src="/logo-mark.png"
                alt="EduExpertAI"
                className="terms-logo-img"
              />
              <h1 className="terms-logo-text">EduExpertAI</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="terms-main">
        <Card className="terms-card">
          <CardHeader className="terms-card-header">
            <CardTitle className="terms-card-title">Terms of Service</CardTitle>
            <p className="terms-last-updated">Last updated: December 2024</p>
          </CardHeader>

          <CardContent className="terms-content">
            <div className="space-y-8">
              {/* Introduction */}
              <section className="terms-section">
                <h2 className="terms-section-title">
                  <BookOpen className="terms-section-icon" />
                  1. Agreement to Terms
                </h2>
                <div className="terms-section-content">
                  <p>
                    Welcome to EduExpertAI, a comprehensive AI-powered educational technology
                    platform ("Platform") operated by EduExpertAI Inc. ("Company", "we", "us", or
                    "our"). These Terms of Service ("Terms") constitute a legally binding agreement
                    between you ("User", "you", or "your") and EduExpertAI regarding your access to
                    and use of our educational platform and services.
                  </p>
                  <p>
                    By accessing, browsing, or using our Platform, you acknowledge that you have
                    read, understood, and agree to be bound by these Terms and our Privacy Policy.
                    If you disagree with any part of these Terms, you must not access or use our
                    Platform.
                  </p>
                </div>
              </section>

              {/* Service Description */}
              <section className="terms-section">
                <h2 className="terms-section-title">
                  <Shield className="terms-section-icon" />
                  2. Platform Services
                </h2>
                <div className="terms-section-content">
                  <p>
                    EduExpertAI provides a comprehensive educational technology platform offering:
                  </p>
                  <ul>
                    <li>
                      <strong>Interactive Learning Management:</strong> Course creation, content
                      delivery, and progress tracking
                    </li>
                    <li>
                      <strong>AI-Powered Assessment Tools:</strong> Automated question generation,
                      grading, and personalized feedback
                    </li>
                    <li>
                      <strong>Real-Time Collaboration:</strong> Virtual classrooms, live sessions,
                      and interactive discussions
                    </li>
                    <li>
                      <strong>Analytics & Reporting:</strong> Learning analytics, performance
                      insights, and institutional reporting
                    </li>
                    <li>
                      <strong>Content Creation Tools:</strong> Course builder, multimedia
                      integration, and assessment design
                    </li>
                    <li>
                      <strong>Student Engagement Features:</strong> Gamification, progress tracking,
                      and social learning
                    </li>
                    <li>
                      <strong>Administrative Tools:</strong> User management, institutional
                      hierarchy, and compliance reporting
                    </li>
                    <li>
                      <strong>Integration Capabilities:</strong> LMS integration, third-party tools,
                      and API access
                    </li>
                  </ul>
                  <p>
                    Our services are designed for educational institutions, corporate training
                    programs, individual educators, and learners seeking advanced educational
                    technology solutions.
                  </p>
                </div>
              </section>

              {/* User Accounts and Eligibility */}
              <section className="terms-section">
                <h2 className="terms-section-title">
                  <Users className="terms-section-icon" />
                  3. User Accounts and Eligibility
                </h2>
                <div className="terms-section-content">
                  <h3>3.1 Account Creation and Eligibility</h3>
                  <p>
                    To access certain features of our Platform, you must create an account. You
                    must:
                  </p>
                  <ul>
                    <li>Be at least 13 years of age (or the minimum age in your jurisdiction)</li>
                    <li>Provide accurate, current, and complete information during registration</li>
                    <li>Maintain and promptly update your account information</li>
                    <li>Have the legal authority to enter into this agreement</li>
                  </ul>

                  <h3>3.2 User Categories and Responsibilities</h3>
                  <div className="space-y-4">
                    <div>
                      <h4>
                        <strong>Students/Learners:</strong>
                      </h4>
                      <ul>
                        <li>Participate in learning activities and assessments</li>
                        <li>Respect intellectual property and academic integrity</li>
                        <li>Engage constructively in collaborative activities</li>
                      </ul>
                    </div>
                    <div>
                      <h4>
                        <strong>Educators/Teachers:</strong>
                      </h4>
                      <ul>
                        <li>Create and manage educational content responsibly</li>
                        <li>Ensure content accuracy and appropriateness</li>
                        <li>Protect student privacy and educational records</li>
                        <li>Comply with institutional and legal requirements</li>
                      </ul>
                    </div>
                    <div>
                      <h4>
                        <strong>Institution Administrators:</strong>
                      </h4>
                      <ul>
                        <li>Manage institutional accounts and user access</li>
                        <li>Ensure compliance with educational regulations</li>
                        <li>Monitor platform usage and maintain security</li>
                        <li>Coordinate with EduExpertAI for institutional needs</li>
                      </ul>
                    </div>
                    <div>
                      <h4>
                        <strong>Corporate Trainers:</strong>
                      </h4>
                      <ul>
                        <li>Develop professional training programs</li>
                        <li>Ensure content relevance and quality</li>
                        <li>Maintain confidentiality of corporate information</li>
                      </ul>
                    </div>
                  </div>

                  <h3>3.3 Account Security</h3>
                  <p>
                    You are responsible for maintaining the confidentiality of your account
                    credentials and for all activities that occur under your account. You must
                    immediately notify us of any unauthorized use of your account or any other
                    security breach.
                  </p>
                </div>
              </section>

              {/* Acceptable Use Policy */}
              <section className="terms-section">
                <h2 className="terms-section-title">
                  <Scale className="terms-section-icon" />
                  4. Acceptable Use Policy
                </h2>
                <div className="terms-section-content">
                  <h3>4.1 Permitted Uses</h3>
                  <p>
                    You may use our Platform only for lawful educational and training purposes,
                    including:
                  </p>
                  <ul>
                    <li>Creating, sharing, and accessing educational content</li>
                    <li>Participating in learning activities and assessments</li>
                    <li>Collaborating with other users for educational purposes</li>
                    <li>Using our tools to enhance teaching and learning experiences</li>
                  </ul>

                  <h3>4.2 Prohibited Activities</h3>
                  <p>You agree not to:</p>
                  <ul>
                    <li>Violate any applicable laws, regulations, or third-party rights</li>
                    <li>
                      Upload, transmit, or share content that is illegal, harmful, threatening,
                      abusive, or discriminatory
                    </li>
                    <li>Engage in academic dishonesty, cheating, or plagiarism</li>
                    <li>
                      Attempt to gain unauthorized access to our systems or other users' accounts
                    </li>
                    <li>Interfere with or disrupt the Platform's functionality or security</li>
                    <li>
                      Use the Platform for commercial purposes outside of your subscription terms
                    </li>
                    <li>Create fake accounts or impersonate others</li>
                    <li>Share explicit, violent, or inappropriate content</li>
                    <li>Engage in bullying, harassment, or discriminatory behavior</li>
                    <li>Reverse engineer, decompile, or attempt to extract source code</li>
                  </ul>
                </div>
              </section>

              {/* Educational Data and Privacy */}
              <section className="terms-section">
                <h2 className="terms-section-title">
                  <Lock className="terms-section-icon" />
                  5. Educational Data Protection and Privacy
                </h2>
                <div className="terms-section-content">
                  <h3>5.1 Commitment to Student Privacy</h3>
                  <p>
                    We are committed to protecting the privacy and security of all users,
                    particularly students and minors. We comply with applicable educational privacy
                    laws, including:
                  </p>
                  <ul>
                    <li>
                      <strong>FERPA (Family Educational Rights and Privacy Act)</strong> - US
                      federal law protecting student educational records
                    </li>
                    <li>
                      <strong>COPPA (Children's Online Privacy Protection Act)</strong> - US law
                      protecting children under 13
                    </li>
                    <li>
                      <strong>GDPR (General Data Protection Regulation)</strong> - European privacy
                      regulation
                    </li>
                    <li>
                      <strong>State and local privacy laws</strong> as applicable
                    </li>
                  </ul>

                  <h3>5.2 Educational Data Usage</h3>
                  <p>Educational data collected through our Platform is used exclusively for:</p>
                  <ul>
                    <li>Providing educational services and platform functionality</li>
                    <li>Improving learning outcomes and educational experiences</li>
                    <li>Generating anonymized analytics and insights</li>
                    <li>Ensuring platform security and preventing misuse</li>
                    <li>Complying with legal and regulatory requirements</li>
                  </ul>
                  <p>
                    We do not sell student data, use it for advertising purposes, or share it with
                    third parties except as necessary to provide our services or as required by law.
                  </p>

                  <h3>5.3 Data Security Measures</h3>
                  <p>We implement comprehensive security measures including:</p>
                  <ul>
                    <li>Industry-standard encryption for data in transit and at rest</li>
                    <li>Secure, SOC 2 Type II compliant data centers</li>
                    <li>Regular security audits and vulnerability assessments</li>
                    <li>Multi-factor authentication and access controls</li>
                    <li>Employee training on data protection and privacy</li>
                  </ul>
                </div>
              </section>

              {/* Intellectual Property */}
              <section className="terms-section">
                <h2 className="terms-section-title">
                  <Star className="terms-section-icon" />
                  6. Intellectual Property Rights
                </h2>
                <div className="terms-section-content">
                  <h3>6.1 Platform Ownership</h3>
                  <p>
                    EduExpertAI and its licensors own all rights, title, and interest in the
                    Platform, including all software, technology, designs, trademarks, and
                    proprietary content. These Terms grant you a limited, non-exclusive,
                    non-transferable license to use the Platform in accordance with these Terms.
                  </p>

                  <h3>6.2 User-Generated Content</h3>
                  <p>
                    You retain ownership of content you create using our Platform ("User Content").
                    By uploading or creating User Content, you grant us a worldwide, royalty-free,
                    non-exclusive license to use, reproduce, modify, and distribute your content
                    solely for the purpose of providing our services.
                  </p>

                  <h3>6.3 Educational Content and Fair Use</h3>
                  <p>
                    Users must respect intellectual property rights and fair use principles when
                    creating and sharing educational content. We support educational fair use but
                    require users to comply with applicable copyright laws and institutional
                    policies.
                  </p>
                </div>
              </section>

              {/* Disclaimers and Limitations */}
              <section className="terms-section">
                <h2 className="terms-section-title">
                  <AlertTriangle className="terms-section-icon" />
                  7. Disclaimers and Limitations of Liability
                </h2>
                <div className="terms-section-content">
                  <h3>7.1 Service Availability</h3>
                  <p>
                    While we strive for continuous availability, we do not guarantee that our
                    Platform will be available 24/7 or free from errors. We may temporarily suspend
                    access for maintenance, updates, or to address security issues.
                  </p>

                  <h3>7.2 Educational Outcomes</h3>
                  <p>
                    While our Platform is designed to enhance learning experiences, we do not
                    guarantee specific educational outcomes or academic success. Learning results
                    depend on various factors including individual effort, institutional support,
                    and proper platform usage.
                  </p>

                  <h3>7.3 Limitation of Liability</h3>
                  <p>
                    To the fullest extent permitted by law, EduExpertAI shall not be liable for any
                    indirect, incidental, special, consequential, or punitive damages arising from
                    your use of the Platform, including but not limited to loss of data, lost
                    profits, or business interruption.
                  </p>
                </div>
              </section>

              {/* Termination */}
              <section className="terms-section">
                <h2 className="terms-section-title">8. Termination</h2>
                <div className="terms-section-content">
                  <p>
                    Either party may terminate this agreement at any time. We may suspend or
                    terminate your access for violation of these Terms, non-payment, or other
                    reasonable cause. Upon termination, you may export your data for a reasonable
                    period as specified in our data retention policy.
                  </p>
                </div>
              </section>

              {/* Governing Law */}
              <section className="terms-section">
                <h2 className="terms-section-title">9. Governing Law and Dispute Resolution</h2>
                <div className="terms-section-content">
                  <p>
                    These Terms are governed by the laws of the State of California, USA. Any
                    disputes arising from these Terms or your use of the Platform will be resolved
                    through binding arbitration in accordance with the rules of the American
                    Arbitration Association.
                  </p>
                </div>
              </section>

              {/* Changes to Terms */}
              <section className="terms-section">
                <h2 className="terms-section-title">10. Changes to These Terms</h2>
                <div className="terms-section-content">
                  <p>
                    We may update these Terms from time to time to reflect changes in our services,
                    legal requirements, or business practices. We will notify users of material
                    changes via email or platform notification at least 30 days before the changes
                    take effect. Continued use of the Platform after changes constitutes acceptance
                    of the updated Terms.
                  </p>
                </div>
              </section>

              {/* Contact Information */}
              <section className="terms-section">
                <h2 className="terms-section-title">Contact Information</h2>
                <div className="terms-contact">
                  <div className="terms-contact-title">
                    If you have questions about these Terms of Service, please contact us:
                  </div>
                  <p className="terms-contact-text">
                    <strong>Legal Department:</strong>{' '}
                    <a href="mailto:legal@eduexpertai.com" className="terms-contact-email">
                      legal@eduexpertai.com
                    </a>
                    <br />
                    <strong>Customer Support:</strong>{' '}
                    <a href="mailto:support@eduexpertai.com" className="terms-contact-email">
                      support@eduexpertai.com
                    </a>
                    <br />
                    <strong>Address:</strong> EduExpertAI Inc , Vijaynagara bangalore
                    <br />
                    <strong>Phone:</strong> +91 7483868010
                    <br />
                    <strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM PST
                  </p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>

        {/* Back to signup */}
        <div className="text-center mt-8">
          <Link to="/signup">
            <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
              Back to Sign Up
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
